import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertOctagon, AlertTriangle, ArrowUpRight, Target } from "lucide-react";

import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMxn } from "@/components/reports/KpiCard";

/**
 * Compact card that appears on Dashboard ONLY when there are categories in
 * warning/over state for the current MX month.
 */
export function BudgetAlertCard({ branchId }) {
  const navigate = useNavigate();
  const { isSuperAdmin, activeTenantId } = useTenant();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = {};
    if (branchId) p.branch_id = branchId;
    if (isSuperAdmin && activeTenantId) p.tenant_id = activeTenantId;
    return p;
  }, [branchId, isSuperAdmin, activeTenantId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/reports/budget-vs-actual", { params });
        if (!cancelled) setReport(data);
      } catch (_) {
        if (!cancelled) setReport(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [params]);

  const over = report?.over_categories || [];
  const warn = report?.warning_categories || [];
  if (loading) return <Skeleton className="h-16" data-testid="budget-alert-skeleton" />;
  // Silent when nothing to alert (per user's spec: no visual noise)
  if (!report || (over.length === 0 && warn.length === 0)) return null;

  const topAlerts = [...over.slice(0, 2), ...warn.slice(0, 2)].slice(0, 3);

  return (
    <div
      className={`border rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${
        over.length > 0 ? "border-red-200 bg-red-50/40" : "border-amber-200 bg-amber-50/40"
      }`}
      data-testid="budget-alert"
    >
      <div className="flex items-start gap-3 flex-1">
        <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${
          over.length > 0 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
        }`}>
          <Target className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`text-sm font-semibold ${
              over.length > 0 ? "text-red-950" : "text-amber-950"
            }`}>
              Categorías cerca o encima del presupuesto
            </h3>
            {over.length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-800 border border-red-200"
                    data-testid="budget-alert-over">
                {over.length} sobre presupuesto
              </span>
            )}
            {warn.length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-800 border border-amber-200"
                    data-testid="budget-alert-warning">
                {warn.length} en warning
              </span>
            )}
          </div>
          <div className={`text-xs mt-1 ${over.length > 0 ? "text-red-900/80" : "text-amber-900/80"}`}>
            Período {report.period} · Presupuesto {formatMxn(report.totals?.budget)} · Real{" "}
            {formatMxn(report.totals?.actual)} · <b>{report.totals?.used_pct_display}%</b> consumido
          </div>

          {topAlerts.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="budget-alert-preview">
              {topAlerts.map((c) => {
                const isOver = c.status === "over";
                const cls = isOver
                  ? "bg-red-100 text-red-800 border-red-200"
                  : "bg-amber-100 text-amber-800 border-amber-200";
                const Icon = isOver ? AlertOctagon : AlertTriangle;
                return (
                  <span
                    key={c.category_id}
                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${cls}`}
                    title={c.category_name}
                  >
                    <Icon className="h-3 w-3" />
                    {c.category_name} · <b>{c.used_pct_display}%</b>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Button
        variant="outline"
        size="sm"
        className="bg-white shrink-0"
        onClick={() => navigate("/gastos/presupuesto")}
        data-testid="budget-alert-open"
      >
        Ver presupuesto <ArrowUpRight className="h-4 w-4 ml-1" />
      </Button>
    </div>
  );
}
